import React, { useEffect, useRef } from 'react';
import ImageCropDetailMediaTile from './ImageCropDetailMediaTile';
import {
  buildImageCropDetectLogKey,
  logImageCropDetectInfo,
} from '../utils/imageCropDetailInfo';
import {
  buildCropDetailResultSelectKey,
  resolveCropResultTiles,
  resolveCropTilePreview,
  rowHasMultipleCropResultTiles,
} from '../utils/imageCropDetailPrints';

function mapCroppedStateToTileState(croppedState) {
  if (croppedState === 'ready') return 'ready';
  if (croppedState === 'loading') return 'loading';
  if (croppedState === 'error') return 'error';
  return 'pending';
}

/** 每张原图下的裁剪效果从 1 起递增编号 */
function buildCropEffectLabel(resultIndex) {
  return `裁剪效果${resultIndex}`;
}

/**
 * 图案裁剪详情 · 结果区（对齐智能抠图 goods 布局 outputs-row）
 */
export default function ImageCropDetailCropResults({
  img,
  rowIndex = 1,
  croppedState = 'pending',
  croppedLoadFailed = {},
  setCroppedLoadFailed,
  referencePreviewByImageId = new Map(),
  selectedTileKeys = new Set(),
  isDisabled,
  onRestoreDisabled,
  onToggleSelect,
  onPreview,
  onEdit,
  onDownload,
  onRetry,
  retrying = false,
  taskId = '',
  outputIndexByTileKey = null,
}) {
  const loggedDetectKeysRef = useRef(new Set());

  useEffect(() => {
    const status = img?.status;
    if (!img || status === 'pending' || status === 'processing') return;
    const logKey = buildImageCropDetectLogKey(img);
    if (!logKey || loggedDetectKeysRef.current.has(logKey)) return;
    loggedDetectKeysRef.current.add(logKey);
    logImageCropDetectInfo(img);
  }, [img]);

  const tiles = resolveCropResultTiles(img);
  const isMulti = rowHasMultipleCropResultTiles(img);
  const tileState = mapCroppedStateToTileState(croppedState);

  const isTileSelected = (tileKey) => selectedTileKeys.has(buildCropDetailResultSelectKey(tileKey));
  const emptySelectKey = buildCropDetailResultSelectKey(`${String(img?.imageId || 'unknown')}-empty`);

  const renderResultTile = (tile, preview, resultIndex, options = {}) => {
    const {
      usesOriginal = false,
      printMeta = null,
    } = options;

    const resultLabel = buildCropEffectLabel(resultIndex);
    const resultUrl = preview.url;
    const displayUrl = preview.displayUrl || preview.viewUrl || resultUrl;
    const previewName = usesOriginal ? `原图 ${rowIndex}（未识别到图案）` : resultLabel;
    const hasSavedCrop = tile?.hasSavedCrop !== false && Boolean(resultUrl);
    const canShowResult = tileState === 'ready' && (hasSavedCrop || Boolean(resultUrl));
    const resolvedTileState = tileState === 'ready' && !resultUrl ? 'pending' : tileState;

    const resultSelectKey = buildCropDetailResultSelectKey(tile.tileKey);
    const tileDisabled = typeof isDisabled === 'function' ? isDisabled(resultSelectKey) : false;
    const sourceImageId = String(img?.imageId || '').trim();
    const outputIndex = outputIndexByTileKey instanceof Map
      ? outputIndexByTileKey.get(tile.tileKey)
      : (Number.isFinite(Number(tile?.printIndex)) ? Number(tile.printIndex) : null);
    const imageName = usesOriginal
      ? `原图_${sourceImageId || rowIndex}`
      : (tile?.label || `图案裁剪-${Number.isFinite(Number(outputIndex)) ? Number(outputIndex) + 1 : resultIndex}`);

    return (
      <ImageCropDetailMediaTile
        key={`${tile.tileKey}__${img?.resultRevision ?? 0}`}
        label={resultLabel}
        url={canShowResult ? resultUrl : ''}
        displayUrl={canShowResult ? displayUrl : ''}
        imageId={usesOriginal ? sourceImageId : tile.imageId}
        imageSpaceId={usesOriginal ? sourceImageId : ''}
        candidateIds={usesOriginal ? [sourceImageId] : []}
        imageName={imageName}
        navId="imageCrop"
        taskId={taskId}
        outputIndex={Number.isFinite(Number(outputIndex)) ? Number(outputIndex) : null}
        alt={previewName}
        state={resolvedTileState}
        fallbackTip={usesOriginal ? '未识别到图案，显示原图' : ''}
        selected={isTileSelected(tile.tileKey)}
        selectable
        selectKey={resultSelectKey}
        disabled={tileDisabled}
        onRestoreDisabled={() => onRestoreDisabled?.(resultSelectKey)}
        onToggleSelect={onToggleSelect}
        editOnHover
        cutoutOutputTile
        errorMessage={img.error}
        retrying={retrying}
        onError={!usesOriginal
          ? () => setCroppedLoadFailed?.((prev) => ({ ...prev, [tile.tileKey]: true }))
          : undefined}
        onPreview={(url, name, extra) => onPreview?.(url, name, {
          ...extra,
          id: preview.id,
          displayUrl,
          viewUrl: displayUrl,
        })}
        onEdit={() => onEdit?.(img, {
          title: `调整${resultLabel}`,
          printIndex: isMulti ? tile?.printIndex : null,
          label: null,
          confidence: printMeta?.confidence ?? img.cropPosition?.confidence,
        })}
        onDownload={() => onDownload?.(resultUrl, tile.imageId)}
        onRetry={() => onRetry?.(img)}
      />
    );
  };

  if (tiles.length === 0) {
    const emptyDisabled = typeof isDisabled === 'function' ? isDisabled(emptySelectKey) : false;
    return (
      <div className="img2img-detail__outputs-row">
        <ImageCropDetailMediaTile
          label={buildCropEffectLabel(1)}
          state={tileState}
          errorMessage={img.error}
          selected={selectedTileKeys.has(emptySelectKey)}
          selectable
          selectKey={emptySelectKey}
          disabled={emptyDisabled}
          onRestoreDisabled={() => onRestoreDisabled?.(emptySelectKey)}
          onToggleSelect={onToggleSelect}
          editOnHover
          cutoutOutputTile
          retrying={retrying}
          onEdit={() => onEdit?.(img, { title: `调整${buildCropEffectLabel(1)}` })}
          onRetry={() => onRetry?.(img)}
        />
      </div>
    );
  }

  return (
    <div className={`img2img-detail__outputs-row${isMulti ? ' img2img-detail__outputs-row--multi' : ''}`}>
      {tiles.map((tile, index) => {
        const preview = resolveCropTilePreview(img, tile, {
          croppedLoadFailed,
          referencePreviewByImageId,
        });
        const usesOriginal = preview.usesOriginal;
        const printMeta = Array.isArray(img.cropPosition?.prints)
          ? img.cropPosition.prints.find(
            (item) => Number(item?.printIndex ?? item?.print_index) === Number(tile.printIndex),
          )
          : null;

        return renderResultTile(tile, preview, index + 1, {
          usesOriginal,
          printMeta,
        });
      })}
    </div>
  );
}
